import { AsyncPipe, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, HostListener, inject, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { FormFieldModule } from '../../wrapper/form-field/form-field.component';
import { CyTestDirective } from '@portal/directive';
import { PortalFieldTypeConfig } from '@portal/form/data-access';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_SELECT_SCROLL_STRATEGY, MatSelectModule } from '@angular/material/select';
import { FormlySelectModule, FormlySelectOption, FormlySelectOptionsPipe } from '@ngx-formly/core/select';
import { BlockScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { isObservable, map, take } from 'rxjs';
import { SelectModule } from '../../component';
import { EMPTY_VALUE } from '@core/data-access';

function scrollFactory(overlay: Overlay): () => BlockScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

@Component({
  selector: 'portal-form-field-select',
  standalone: true,
  imports: [
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    AsyncPipe,
    NgForOf,
    AsyncPipe,
    FormlyModule,
    FormlySelectModule,
    MatSelectModule,
    ReactiveFormsModule,
    CyTestDirective,
    MatCheckboxModule,
    SelectModule,
  ],
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    FormlySelectOptionsPipe,
  ],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent extends FieldType<PortalFieldTypeConfig> {
  private readonly el = inject(ElementRef<HTMLElement>);
  protected readonly defaultValue = EMPTY_VALUE;

  get isObservableValue() {
    return isObservable(this.value);
  }

  @HostBinding('class') get hostClass(): string {
    return this.disabled ? 'field-value' : 'field-input-wrapper';
  }

  /**
   * Check if form or field is disabled
   * @returns boolean
   * @readonly
   */
  get disabled(): boolean {
    return !!this.options?.formState?.disabled || !!this.props?.disabled;
  }

  get value() {
    if (this.disabled) {
      if (isObservable(this.props?.options)) {
        return this.props?.options?.pipe(
          take(1),
          map(options => {
            const option = options.find(option => option.value === this.formControl.value);
            return option?.label;
          }),
        );
      } else {
        const option = this.props?.options?.find(option => option.value === this.formControl.value);
        return option?.label;
      }
    }
    return null;
  }

  compareFn(c1: unknown, c2: unknown): boolean {
    return c1 === c2;
  }

  getObsValue(options: FormlySelectOption[]) {
    return this.props?.multiple ? this.renderMultiValue(options) : this.renderSingleValue(options);
  }

  renderViewValue(value: any) {
    if (this.props?.displayWith) {
      return this.displayValue(value) || this.defaultValue;
    }

    if (Array.isArray(this.props?.options)) {
      return this.props?.multiple ? this.renderMultiValue(this.props?.options) : this.renderSingleValue(this.props?.options);
    }

    return this.displayValue(value);
  }

  private renderMultiValue(options: FormlySelectOption[]): string {
    const value = this.formControl.value;
    if (!Array.isArray(value)) return this.displayValue(value);

    return options
      .filter(option => value.includes(option.value))
      .map(option => this.displayValue(option.label))
      .join(', ');
  }

  private renderSingleValue(options: FormlySelectOption[]): string {
    const value = this.formControl.value;
    const optionValue = options.find(option => option.value === value)?.label;
    return this.displayValue(optionValue || value);
  }

  private displayValue(value: any) {
    return this.props?.displayWith?.(value) || value || this.defaultValue;
  }

  @HostListener('mouseenter') private onMouseEnter(): void {
    this.el.nativeElement.setAttribute('title', this.renderViewValue(this.formControl.value) || this.props?.label);
  }

  @HostListener('mouseleave') private onMouseLeave(): void {
    this.el.nativeElement.removeAttribute('title');
  }
}

@NgModule({
  imports: [
    FormFieldModule,
    FormlyModule.forChild({
      types: [{ name: 'select', component: SelectComponent, wrappers: ['form-field'] }],
    }),
  ],
})
export class FormSelectModule {}
